<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useFollowUp } from 'dashboard/components-next/FollowUp/composables/useFollowUp.js';

// Single source of truth for "complete a follow-up, then ask whether to
// schedule another" — reused by both the edit dialog and the sidebar
// summary card so the flow can't drift between the two entry points.
const props = defineProps({
  contactId: { type: Number, required: true },
  size: { type: String, default: 'sm' },
});

const emit = defineEmits(['completed', 'reschedule']);

const { t } = useI18n();
const { completeFollowUp, isSaving } = useFollowUp();

const rescheduleDialogRef = ref(null);

const handleComplete = async () => {
  const completed = await completeFollowUp(props.contactId);
  if (completed) {
    emit('completed');
    rescheduleDialogRef.value?.open();
  }
};

const handleRescheduleConfirm = () => {
  rescheduleDialogRef.value?.close();
  emit('reschedule');
};
</script>

<template>
  <Button
    :label="t('FOLLOW_UP.ACTIONS.COMPLETE')"
    variant="faded"
    color="slate"
    :size="size"
    :is-loading="isSaving"
    @click="handleComplete"
  />

  <Dialog
    ref="rescheduleDialogRef"
    type="alert"
    :title="t('FOLLOW_UP.RESCHEDULE_DIALOG.TITLE')"
    :description="t('FOLLOW_UP.RESCHEDULE_DIALOG.DESCRIPTION')"
    :cancel-button-label="t('FOLLOW_UP.RESCHEDULE_DIALOG.NO')"
    :confirm-button-label="t('FOLLOW_UP.RESCHEDULE_DIALOG.YES')"
    @confirm="handleRescheduleConfirm"
  />
</template>
